import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Monitor, Sun, Moon, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/PageHeader";
import { UserAvatar } from "@/components/common/UserAvatar";
import { DefaultsFields } from "@/components/common/DefaultsFields";
import { PasswordInput } from "@/components/common/PasswordInput";
import { FormAlert } from "@/components/common/Alert";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { cn } from "@/lib/utils";

const ZONES = (() => {
  const list = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : ["Europe/London"];
  return list.includes("Europe/London") ? list : ["Europe/London", ...list];
})();

const Section = ({ title, description, children, testid }) => (
  <section className="card-elevated p-6 md:p-8" data-testid={testid}>
    <h2 className="text-base font-semibold">{title}</h2>
    {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
    <div className="mt-6">{children}</div>
  </section>
);

const DetailsSection = () => {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: user.name, service_line: user.service_line, audience_stream: user.audience_stream, region: user.region, current_campaign: user.current_campaign || "", timezone: user.timezone || "Europe/London",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.patch("/me", form);
      setUser(data);
      toast.success("Profile saved");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/me/avatar", body);
      setUser(data);
      toast.success("Avatar updated");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Section title="Your details and defaults" description="Your defaults are passed to every run unless you change them for that run." testid="profile-details-section">
      <form onSubmit={save} className="space-y-6">
        <FormAlert testid="profile-details-error">{error}</FormAlert>
        <div className="flex items-center gap-4">
          <UserAvatar user={user} className="h-16 w-16 text-base" testid="profile-avatar" />
          <div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" id="avatar-upload" onChange={(e) => upload(e.target.files?.[0])} data-testid="profile-avatar-input" />
            <Button type="button" variant="secondary" size="sm" onClick={() => fileRef.current?.click()} data-testid="profile-avatar-upload-button"><Upload className="!h-4 !w-4" /> Change avatar</Button>
            <p className="mt-1 text-xs text-muted-foreground">PNG, JPG or WebP, up to 2 MB.</p>
          </div>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="profile-name">Name</Label>
            <Input id="profile-name" value={form.name} maxLength={120} onChange={(e) => onChange("name", e.target.value)} data-testid="profile-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-email">Work email</Label>
            <Input id="profile-email" value={user.email} disabled data-testid="profile-email-input" />
          </div>
        </div>
        <p className="text-sm text-muted-foreground" data-testid="profile-role">Role: <span className="font-medium text-foreground">{ROLE_LABELS[user.role]}</span></p>
        <p className="flex items-start gap-2 rounded-xl bg-muted px-3 py-2 text-sm" data-testid="profile-privacy-note"><ShieldCheck className="!h-4 !w-4 mt-0.5 shrink-0" aria-hidden="true" />Owners can see how many runs you start and on which agents, never what they contain.</p>
        <DefaultsFields values={form} onChange={onChange} />
        <div className="max-w-md space-y-2">
          <Label htmlFor="profile-campaign">Current campaign</Label>
          <Input id="profile-campaign" value={form.current_campaign} maxLength={120} onChange={(e) => onChange("current_campaign", e.target.value)} placeholder="None" data-testid="profile-campaign-input" />
          <p className="text-xs text-muted-foreground">Used when you sign in. You can switch it for a session from the top bar.</p>
        </div>
        <div className="max-w-md space-y-2">
          <Label htmlFor="profile-timezone">Time zone</Label>
          <select id="profile-timezone" value={form.timezone} onChange={(e) => onChange("timezone", e.target.value)} className="flex h-10 w-full rounded-xl border border-input bg-card px-3 text-sm" data-testid="profile-timezone-select">
            {ZONES.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
          </select>
          <p className="text-xs text-muted-foreground">Every time in the hub, including run titles, uses this zone.</p>
        </div>
        <Button type="submit" disabled={busy || !form.name.trim()} data-testid="profile-save-button">{busy ? "Saving" : "Save changes"}</Button>
      </form>
    </Section>
  );
};

const THEMES = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

const ThemeSection = () => {
  const { user, setUser } = useAuth();
  const choose = async (theme) => {
    try {
      const { data } = await api.patch("/me", { theme });
      setUser(data);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <Section title="Appearance" description="System follows your device setting." testid="profile-theme-section">
      <div role="radiogroup" aria-label="Theme" className="flex flex-wrap gap-3">
        {THEMES.map(({ value, label, icon: Icon }) => (
          <button key={value} type="button" role="radio" aria-checked={user.theme === value} onClick={() => choose(value)}
            className={cn("flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm transition-quick hover:bg-muted", user.theme === value && "border-foreground font-medium")}
            data-testid={`theme-option-${value}`}>
            <Icon className="!h-4 !w-4" aria-hidden="true" /> {label}
          </button>
        ))}
      </div>
    </Section>
  );
};

const PasswordSection = () => {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next.length < 12) return setError("Use at least 12 characters.");
    if (form.next !== form.confirm) return setError("The two new passwords do not match.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success(data.message);
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Change password" description="Changing your password signs you out everywhere else." testid="profile-password-section">
      <form onSubmit={submit} className="max-w-md space-y-5">
        <FormAlert testid="change-password-error">{error}</FormAlert>
        <PasswordInput id="current-password" label="Current password" value={form.current} onChange={set("current")} testid="change-password-current-input" />
        <PasswordInput id="new-password" label="New password" value={form.next} onChange={set("next")} autoComplete="new-password" hint="At least 12 characters. Avoid common passwords." testid="change-password-new-input" />
        <PasswordInput id="confirm-new-password" label="Type the new password again" value={form.confirm} onChange={set("confirm")} autoComplete="new-password" testid="change-password-confirm-input" />
        <Button type="submit" disabled={busy || !form.current || !form.next || !form.confirm} data-testid="change-password-submit-button">{busy ? "Changing" : "Change password"}</Button>
      </form>
    </Section>
  );
};

export default function Profile() {
  return (
    <div data-testid="profile-page">
      <PageHeader title="Profile" description="Your details, defaults, appearance and password." />
      <div className="grid max-w-4xl gap-6">
        <DetailsSection />
        <ThemeSection />
        <PasswordSection />
      </div>
    </div>
  );
}
